// Fonte única de SEO: usada pelo React e transpilada por scripts/prerender.mjs (apenas imports de tipo).
import type { Article } from "../data/articles";

export const SITE_URL = "https://gepeto.life";

export const LEAD_LABEL = "Síntese Regulatória Expressa";

type JsonLd = Record<string, unknown>;

export const articleUrl = (a: Pick<Article, "niche" | "slug">): string => `${SITE_URL}/${a.niche}/${a.slug}`;

export const articleTitle = (a: Pick<Article, "title" | "metaTitle">): string => a.metaTitle ?? `${a.title} | Gepeto.life`;

export const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

// Trunca sem cortar palavra; acrescenta "…" (conta no limite) quando truncado.
export function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const i = cut.lastIndexOf(" ");
  const base = (i > 0 ? cut.slice(0, i) : cut.slice(0, max - 1)).replace(/[\s,;:.\-–—(]+$/, "");
  return `${base}…`;
}

export const articleDescription = (a: Pick<Article, "legalSummary" | "metaDescription">): string =>
  a.metaDescription ?? truncateAtWord(stripHtml(a.legalSummary), 160);

export const articleOgDescription = (a: Pick<Article, "legalSummary" | "metaDescription">): string =>
  a.metaDescription ?? truncateAtWord(stripHtml(a.legalSummary), 200);

export const lazyImages = (html: string): string => html.replace(/<img\b(?![^>]*\bloading=)/gi, '<img loading="lazy"');

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export const articleOgImageUrl = (a: Pick<Article, "title" | "niche" | "nicheLabel">): string =>
  `${SITE_URL}/api/og?title=${encodeURIComponent(a.title)}&niche=${encodeURIComponent(a.niche)}&category=${encodeURIComponent(a.nicheLabel)}`;

export function formatPublishedDate(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
}

// CTA inline após a 2ª seção: divide no início do 3º heading de seção (h2 ou h3).
export function splitContent(html: string): [string, string] {
  const starts = Array.from(html.matchAll(/<h[23][\s>]/g), (m) => m.index ?? 0);
  if (starts.length < 3) return [html, ""];
  return [html.slice(0, starts[2]), html.slice(starts[2])];
}

export function buildArticleJsonLd(a: Article): JsonLd[] {
  const url = articleUrl(a);
  return [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      description: a.subtitle,
      datePublished: a.publishedAt,
      dateModified: a.publishedAt,
      author: { "@type": "Organization", name: "Gepeto Tecnologia" },
      publisher: { "@type": "Organization", name: "Gepeto.life", url: SITE_URL },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      inLanguage: "pt-BR",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: a.nicheLabel, item: `${SITE_URL}${a.nicheUrl}` },
        { "@type": "ListItem", position: 3, name: a.title, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: a.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    },
  ];
}

export const serializeJsonLd = (data: unknown): string => JSON.stringify(data).replace(/</g, "\\u003c");

export interface HomeOffer {
  name: string;
  url: string;
  price: number;
}

export interface OrgInfo {
  email: string;
  cnpj: string;
  address: Record<string, string>;
}

const ORG_ID = `${SITE_URL}/#organization`;

export function buildOrganizationJsonLd(org: OrgInfo): JsonLd {
  return {
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Gepeto Tecnologia",
    url: SITE_URL,
    description: "Plataforma SaaS utilitária independente de tecnologia jurídica autônoma para emissão de notificações extrajudiciais formais.",
    email: org.email,
    taxID: org.cnpj,
    address: { "@type": "PostalAddress", ...org.address },
  };
}

export function buildAboutJsonLd(org: OrgInfo, name: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "AboutPage", "@id": `${SITE_URL}/sobre#webpage`, name, url: `${SITE_URL}/sobre`, inLanguage: "pt-BR", about: { "@id": ORG_ID } },
      buildOrganizationJsonLd(org),
    ],
  };
}

export function buildHomeJsonLd(org: OrgInfo, offers: HomeOffer[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebSite", "@id": `${SITE_URL}/#website`, name: "Gepeto Tecnologia", url: SITE_URL, inLanguage: "pt-BR", publisher: { "@id": ORG_ID } },
      {
        ...buildOrganizationJsonLd(org),
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Notificações extrajudiciais",
          itemListElement: offers.map((o) => ({
            "@type": "Offer",
            url: `${SITE_URL}${o.url}`,
            price: o.price.toFixed(2),
            priceCurrency: "BRL",
            itemOffered: { "@type": "Service", name: o.name, url: `${SITE_URL}${o.url}` },
          })),
        },
      },
    ],
  };
}
